import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Info } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const strategySchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  algo_type: z.string().min(1, "Algorithm is required"),
  description: z.string().max(500).optional().default(""),
  symbol_scope: z.string().optional().default(""),
  schedule_type: z.enum(["interval", "cron"]).default("interval"),
  schedule_value: z.string().min(1, "Schedule value is required"),
  webhook_source_id: z.string().optional().nullable().default(null),
  algo_config: z.record(z.any()).optional().default({
    trailing_stop_pct: 5.0,
    trailing_activation_pct: 5.0,
    profit_booking_pct: 20.0,
    profit_booking_qty_pct: 50.0,
  }),
  enabled: z.boolean().default(true),
});

export default function StrategiesSheet({
  open,
  onOpenChange,
  onSubmit,
  editingStrategy,
  algorithms = [],
  webhookSources = [],
}) {
  const isEdit = !!editingStrategy;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(strategySchema),
    defaultValues: {
      name: "",
      algo_type: "",
      description: "",
      symbol_scope: "",
      schedule_type: "interval",
      schedule_value: "",
      webhook_source_id: null,
      algo_config: {
        trailing_stop_pct: 5.0,
        trailing_activation_pct: 5.0,
        profit_booking_pct: 20.0,
        profit_booking_qty_pct: 50.0,
      },
      enabled: true,
    },
  });

  useEffect(() => {
    if (editingStrategy) {
      reset({
        name: editingStrategy.name || "",
        algo_type: editingStrategy.algo_type || "",
        description: editingStrategy.description || "",
        symbol_scope: editingStrategy.symbol_scope || "",
        schedule_type: editingStrategy.schedule_type || "interval",
        schedule_value: editingStrategy.schedule_value || "",
        webhook_source_id: editingStrategy.webhook_source_id || null,
        algo_config: {
          trailing_stop_pct: 5.0,
          trailing_activation_pct: 5.0,
          profit_booking_pct: 20.0,
          profit_booking_qty_pct: 50.0,
          ...(editingStrategy.algo_config || {}),
        },
        enabled: editingStrategy.enabled ?? true,
      });
    } else {
      reset({
        name: "",
        algo_type: "",
        description: "",
        symbol_scope: "",
        schedule_type: "interval",
        schedule_value: "",
        webhook_source_id: null,
        algo_config: {
          trailing_stop_pct: 5.0,
          trailing_activation_pct: 5.0,
          profit_booking_pct: 20.0,
          profit_booking_qty_pct: 50.0,
        },
        enabled: true,
      });
    }
  }, [editingStrategy, reset]);

  const onFormSubmit = async (data) => {
    const success = await onSubmit(data);
    if (success) onOpenChange(false);
  };

  const scheduleType = watch("schedule_type");
  const algoType = watch("algo_type");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-surface-2 border-border w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Strategy" : "Create Strategy"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update your trading strategy configuration." : "Configure a new trading strategy."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-5 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...register("name")} placeholder="My Strategy" className="rounded-sm bg-surface-1 border-border" />
            {errors.name && <p className="text-xs text-loss">{errors.name.message}</p>}
          </div>

          {/* Algo Type */}
          <div className="space-y-2">
            <Label htmlFor="algo_type">Algorithm</Label>
            <Select
              value={watch("algo_type")}
              onValueChange={(v) => setValue("algo_type", v, { shouldValidate: true })}
            >
              <SelectTrigger className="rounded-sm bg-surface-1 border-border">
                <SelectValue placeholder="Select algorithm" />
              </SelectTrigger>
              <SelectContent className="bg-surface-2 border-border">
                {algorithms.map((algo) => (
                  <SelectItem key={algo.algo_type || algo.id || algo.name} value={algo.algo_type || algo.name}>
                    {algo.name || algo.algo_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.algo_type && <p className="text-xs text-loss">{errors.algo_type.message}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea id="description" {...register("description")} placeholder="Describe the strategy..." className="rounded-sm bg-surface-1 border-border" rows={3} />
          </div>

          {/* Symbol Scope */}
          <div className="space-y-2">
            <Label htmlFor="symbol_scope">Symbol Scope (optional)</Label>
            <Input id="symbol_scope" {...register("symbol_scope")} placeholder="RELIANCE, INFY" className="rounded-sm bg-surface-1 border-border" />
            <p className="text-[11px] text-muted-foreground">Comma-separated symbols or 'all'</p>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="schedule_type">Schedule Type</Label>
              <Select
                value={scheduleType}
                onValueChange={(v) => setValue("schedule_type", v, { shouldValidate: true })}
              >
                <SelectTrigger className="rounded-sm bg-surface-1 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface-2 border-border">
                  <SelectItem value="interval">Interval</SelectItem>
                  <SelectItem value="cron">Cron</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule_value">Schedule Value</Label>
              <Input
                id="schedule_value"
                {...register("schedule_value")}
                placeholder={scheduleType === "interval" ? "60" : "*/5 * * * *"}
                className="rounded-sm bg-surface-1 border-border font-mono text-xs"
              />
              {errors.schedule_value && <p className="text-xs text-loss">{errors.schedule_value.message}</p>}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-3">
            {scheduleType === "interval" ? "Interval in minutes" : "Cron expression"}
          </p>

          {/* Webhook Source */}
          <div className="space-y-2">
            <Label htmlFor="webhook_source_id">Webhook Source (optional)</Label>
            <Select
              value={watch("webhook_source_id") ?? "__none__"}
              onValueChange={(v) => setValue("webhook_source_id", v === "__none__" ? null : v, { shouldValidate: true })}
            >
              <SelectTrigger className="rounded-sm bg-surface-1 border-border">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent className="bg-surface-2 border-border">
                <SelectItem value="__none__">None</SelectItem>
                {webhookSources.map((src) => (
                  <SelectItem key={src.id} value={String(src.id)}>
                    {src.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Algo Config - structured for ema_trailing_sl */}
          {algoType === "ema_trailing_sl" && (
            <div className="space-y-4 border border-border rounded-sm p-3 bg-surface-1">
              <div className="flex items-center gap-1.5">
                <Label className="text-sm font-medium">Exit Settings</Label>
                <Info className="w-3.5 h-3.5 text-muted-foreground" />
              </div>

              {/* Trailing Stop */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Trailing Stop</Label>
                  <Switch
                    checked={(watch("algo_config")?.trailing_stop_pct ?? 5) > 0}
                    onCheckedChange={(v) =>
                      setValue("algo_config", {
                        ...watch("algo_config"),
                        trailing_stop_pct: v ? 5.0 : 0,
                      })
                    }
                  />
                </div>
                {(watch("algo_config")?.trailing_stop_pct ?? 5) > 0 && (
                  <div className="grid grid-cols-2 gap-2 pl-2">
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Trail %</Label>
                      <Input
                        type="number"
                        step={0.5}
                        min={1}
                        max={20}
                        value={watch("algo_config")?.trailing_stop_pct ?? 5}
                        onChange={(e) =>
                          setValue("algo_config", {
                            ...watch("algo_config"),
                            trailing_stop_pct: parseFloat(e.target.value) || 5,
                          })
                        }
                        className="rounded-sm bg-surface-2 border-border text-xs h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Activate at % up</Label>
                      <Input
                        type="number"
                        step={0.5}
                        min={0.5}
                        max={50}
                        value={watch("algo_config")?.trailing_activation_pct ?? 5}
                        onChange={(e) =>
                          setValue("algo_config", {
                            ...watch("algo_config"),
                            trailing_activation_pct: parseFloat(e.target.value) || 5,
                          })
                        }
                        className="rounded-sm bg-surface-2 border-border text-xs h-8"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Profit Booking */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Profit Booking (sell partial)</Label>
                  <Switch
                    checked={(watch("algo_config")?.profit_booking_pct ?? 20) > 0}
                    onCheckedChange={(v) =>
                      setValue("algo_config", {
                        ...watch("algo_config"),
                        profit_booking_pct: v ? 20.0 : 0,
                      })
                    }
                  />
                </div>
                {(watch("algo_config")?.profit_booking_pct ?? 20) > 0 && (
                  <div className="grid grid-cols-2 gap-2 pl-2">
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Sell at % profit</Label>
                      <Input
                        type="number"
                        step={1}
                        min={1}
                        max={200}
                        value={watch("algo_config")?.profit_booking_pct ?? 20}
                        onChange={(e) =>
                          setValue("algo_config", {
                            ...watch("algo_config"),
                            profit_booking_pct: parseFloat(e.target.value) || 20,
                          })
                        }
                        className="rounded-sm bg-surface-2 border-border text-xs h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Sell % of position</Label>
                      <Input
                        type="number"
                        step={5}
                        min={1}
                        max={100}
                        value={watch("algo_config")?.profit_booking_qty_pct ?? 50}
                        onChange={(e) =>
                          setValue("algo_config", {
                            ...watch("algo_config"),
                            profit_booking_qty_pct: parseFloat(e.target.value) || 50,
                          })
                        }
                        className="rounded-sm bg-surface-2 border-border text-xs h-8"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Generic algo_config JSON for non-ema algos */}
          {algoType && algoType !== "ema_trailing_sl" && (
            <div className="space-y-2">
              <Label htmlFor="algo_config">Algorithm Config (JSON)</Label>
              <Textarea
                id="algo_config"
                {...register("algo_config", {
                  setValueAs: (v) => {
                    try { return JSON.parse(v); }
                    catch { return v ? {} : {}; }
                  },
                })}
                placeholder='{ "key": "value" }'
                className="rounded-sm bg-surface-1 border-border font-mono text-xs"
                rows={4}
                defaultValue={JSON.stringify(editingStrategy?.algo_config || {}, null, 2)}
              />
            </div>
          )}

          {/* Enabled */}
          <div className="flex items-center justify-between border border-border rounded-sm p-3 bg-surface-1">
            <Label htmlFor="enabled" className="cursor-pointer">Enabled</Label>
            <Switch
              id="enabled"
              checked={watch("enabled")}
              onCheckedChange={(v) => setValue("enabled", v)}
            />
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm border-border"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="rounded-sm" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Strategy"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
